/**
 * CaseFlow+AI - Demo Users
 * 
 * ⚠️ VIKTIGT:
 * - Dessa användare är ENDAST för demo/test-miljö
 * - Aldrig i produktion!
 * - Inga riktiga emails i prod seed
 * - Tydligt markerat som demo
 */

export interface DemoUser {
  email: string;
  password: string;
  role: 'admin' | 'technician' | 'agent' | 'viewer';
  name: string;
  isDemo: boolean;
  description: string;
}

/**
 * Demo användare för testning av CaseFlow+AI
 * 
 * Dessa konton ska ALDRIG användas i produktion.
 * Vid bygge till prod ska dessa filtreras bort.
 */
const demoUsers: DemoUser[] = [
  {
    email: 'admin@caseflow.demo',
    password: 'demo-admin-2026',
    role: 'admin',
    name: 'Admin User (Demo)',
    isDemo: true,
    description: 'Full access to all features including tenant admin and system health',
  },
  {
    email: 'agent@caseflow.demo',
    password: 'demo-agent-2026',
    role: 'agent',
    name: 'Support Agent (Demo)',
    isDemo: true,
    description: 'Standard agent access - can create and manage tickets, orders, and fault reports',
  },
  {
    email: 'technician@caseflow.demo',
    password: 'demo-tech-2026',
    role: 'technician',
    name: 'Field Technician (Demo)',
    isDemo: true,
    description: 'Technical support access - limited navigation, no admin views',
  },
  {
    email: 'viewer@caseflow.demo',
    password: 'demo-viewer-2026',
    role: 'viewer',
    name: 'Viewer User (Demo)',
    isDemo: true,
    description: 'Read-only access - cannot create, edit, or delete any entities',
  },
];

/**
 * Hämta alla demo användare
 */
export const getDemoUsers = (): DemoUser[] => {
  return demoUsers;
};

/**
 * Hämta demo användare via email
 */
export const getDemoUserByEmail = (email: string): DemoUser | undefined => {
  return demoUsers.find(user => user.email === email);
};

/**
 * Kontrollera om en email är en demo-användare
 */
export const isDemoUser = (email: string): boolean => {
  return demoUsers.some(user => user.email === email);
};

/**
 * Hämta demo-användare efter roll
 */
export const getDemoUsersByRole = (role: string): DemoUser[] => {
  return demoUsers.filter(user => user.role === role);
};

/**
 * Validera demo-inloggningsuppgifter
 * Returnerar användaren om lyckad, annars null
 */
export const validateDemoLogin = (email: string, password: string): DemoUser | null => {
  const user = demoUsers.find(u => u.email === email);
  
  if (!user) return null;
  if (user.password !== password) return null;
  
  return user;
};

/**
 * Demo-konfiguration för UI
 */
export const DEMO_CONFIG = {
  enabled: import.meta.env.VITE_DEMO_MODE === 'true',
  showDemoBanner: true,
  demoBadgeText: '🎭 DEMO',
  warningMessage: 'Du arbetar i demo-läge. Inga riktiga kunddata anvands.',
  allowedDemoDomains: ['caseflow.demo', 'localhost', '127.0.0.1'],
};

export default demoUsers;
